import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { OWNER_MAIL_MAX_RECIPIENTS, type OwnerMailSendResultDto } from '@his/shared';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { useTranslation } from '@/lib/i18n';
import { Dialog } from '@/components/ui/layout';
import { Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { TotpField, isValidTotp } from '../OwnerConfirm';
import { parseAddressList } from './mail-helpers';

export interface ComposeDraft {
  to: string;
  cc: string;
  subject: string;
  text: string;
  inReplyTo?: string;
}

export const EMPTY_DRAFT: ComposeDraft = { to: '', cc: '', subject: '', text: '' };

export function OwnerMailComposeDialog({ draft, onClose }: { draft: ComposeDraft; onClose: () => void }) {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const [form, setForm] = useState<ComposeDraft>(draft);
  const [totpCode, setTotpCode] = useState('');

  const to = parseAddressList(form.to);
  const cc = parseAddressList(form.cc);
  const invalid = [...to.invalid, ...cc.invalid];
  const problem =
    invalid.length > 0
      ? t('owner_mail_not_an_address', { list: invalid.join(', ') })
      : to.valid.length === 0
        ? t('owner_mail_add_recipient')
        : to.valid.length > OWNER_MAIL_MAX_RECIPIENTS || cc.valid.length > OWNER_MAIL_MAX_RECIPIENTS
          ? t('owner_mail_too_many_recipients', { count: OWNER_MAIL_MAX_RECIPIENTS })
          : null;

  const send = useMutation({
    mutationFn: () =>
      post<OwnerMailSendResultDto>('/owner/mail/send', {
        to: to.valid,
        cc: cc.valid,
        subject: form.subject.trim(),
        text: form.text,
        ...(form.inReplyTo ? { inReplyTo: form.inReplyTo } : {}),
        totpCode,
      }),
    onSuccess: () => {
      toast.success(t('owner_mail_sent'));
      onClose();
    },
    onError: (error) => {
      setTotpCode('');
      toast.error(t('owner_mail_send_failed'), describeError(error));
    },
  });

  const ready = problem === null && form.subject.trim().length > 0 && form.text.trim().length > 0 && isValidTotp(totpCode);

  return (
    <Dialog
      open
      onClose={onClose}
      wide
      title={form.inReplyTo ? t('owner_mail_reply') : t('owner_mail_new_mail')}
      description={t('owner_mail_sent_from_owner_mailbox')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('owner_mail_cancel')}
          </Button>
          <Button loading={send.isPending} disabled={!ready} onClick={() => send.mutate()}>
            <Send className="h-4 w-4" /> {t('owner_mail_send')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t('owner_mail_to')} htmlFor="mail-to">
          <Input id="mail-to" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} placeholder="name@example.com, other@example.com" dir="ltr" />
        </Field>
        <Field label={t('owner_mail_cc')} htmlFor="mail-cc">
          <Input id="mail-cc" value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })} dir="ltr" />
        </Field>
        <Field label={t('owner_mail_subject')} htmlFor="mail-subject">
          <Input id="mail-subject" maxLength={300} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
        </Field>
        <Field label={t('owner_mail_message')} htmlFor="mail-body">
          <Textarea id="mail-body" rows={10} maxLength={100_000} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
        </Field>
        {problem && <p className="text-xs text-destructive">{problem}</p>}
        <TotpField value={totpCode} onChange={setTotpCode} />
      </div>
    </Dialog>
  );
}
